import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Skeleton, TableCell, TableRow } from '@mui/material'
import { Stack } from '@mui/system'
import classNames from 'classnames'
import * as R from 'ramda'
import { muiTheme } from '@pbt/pbt-ui-components'
import { getPersonString } from '@pbt/pbt-ui-components/src/utils'

import { ConversationTransport, Type } from '~/api/graphql/generated/types'
import SystemMessageLabel from '~/components/dashboard/communications/common/SystemMessageLabel'
import TransportIcon from '~/components/dashboard/communications/TransportIcon'
import { SendGearIcon as SystemMessageIcon } from '~/components/elements/Icons/generated'
import Typography from '~/components/elements/Typography/Typography'
import {
  MESSAGE_TRANSPORT_NAMES,
  MessageStatus,
  MessageWay,
} from '~/constants/communications'
import { fetchMember } from '~/store/actions/members'
import { getConversationMessagesLoading } from '~/store/reducers/conversations'
import { getMembersIsFetchingMemberById } from '~/store/reducers/members'
import { getUser } from '~/store/reducers/users'
import type { Conversation, ConversationMessage } from '~/types'
import { isPlainTextTransport } from '~/utils/communicationsUtils'

import { communicationsSnapshotColumns } from './communicationsSnapshotColumns'
import MessageDateCell from './MessageDateCell'
import RowSkeleton from './RowSkeleton'
import SubjectCell from './SubjectCell'

import styles from './ConversationRow.module.scss'

const MessageRow = ({
  message,
  conversation,
}: {
  conversation: Conversation
  message: ConversationMessage
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')
  const transport = conversation.transport || ConversationTransport.LogPhoneCall
  const isPlainText = isPlainTextTransport(transport)
  const user = useSelector(getUser(message.creatorId))
  const fetchingCreatorState = useSelector(
    getMembersIsFetchingMemberById(message.creatorId),
  )
  const isMessageLoading = useSelector(getConversationMessagesLoading)
  const isFetchingCreator = fetchingCreatorState?.isLoading
  const fetchCreatorError = fetchingCreatorState?.error

  const userName = getPersonString(user)
  const from =
    message.way === MessageWay.INBOUND ? conversation.recipient?.name : userName
  const isUnread = conversation.lastMessageReadDate
    ? conversation.lastMessageReadDate < message.timestamp &&
      message.way === MessageWay.INBOUND
    : true
  const hasSenderName =
    !isFetchingCreator && !R.isNil(from) && !fetchCreatorError

  const getCurrentRole = () => {
    if (message.way === MessageWay.INBOUND) {
      if (conversation.recipient?.type !== Type.Client) {
        return `, ${t('Common:EXTERNAL_CONTACT')}`
      }
      return `, ${t('Common:PET_PARENT')}`
    }

    return ''
  }

  const SubjectLine = (
    <Stack direction="row" gap={0.5} sx={{ alignItems: 'center' }}>
      {message.isSystemMessage ? (
        <SystemMessageLabel
          color={muiTheme.colors.grayGray2}
          fontSize="Paragraph"
          icon={<SystemMessageIcon sx={{ color: muiTheme.colors.grayGray3 }} />}
        />
      ) : (
        <Typography.Paragraph
          className={classNames({
            [styles.systemMessageSubject]: message.isSystemMessage,
            [styles.loadingUser]: isFetchingCreator,
          })}
          sx={{ whiteSpace: 'nowrap', fontWeight: 'fontWeightMedium' }}
        >
          {isFetchingCreator ? (
            <Skeleton data-testid="loading-user-skeleton" variant="text" />
          ) : (
            <>
              {from}
              {getCurrentRole()}
            </>
          )}
        </Typography.Paragraph>
      )}
      {hasSenderName && (
        <Typography.Paragraph
          className={classNames({
            [styles.systemMessageSubject]: message.isSystemMessage,
          })}
        >
          •
        </Typography.Paragraph>
      )}
      <TransportIcon
        classes={{
          transportIcon: classNames({
            [styles.transportIcon]: !message.isSystemMessage,
          }),
        }}
        type={conversation.transport}
      />
      <Typography.Paragraph
        className={classNames({
          [styles.systemMessageSubject]: message.isSystemMessage,
        })}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {MESSAGE_TRANSPORT_NAMES[transport]}
      </Typography.Paragraph>
    </Stack>
  )

  useEffect(() => {
    if (
      R.isNil(user) &&
      message.creatorId &&
      !isFetchingCreator &&
      !fetchCreatorError
    ) {
      dispatch(fetchMember(message.creatorId, true))
    }
  }, [message.creatorId, isFetchingCreator])
  if (isMessageLoading) {
    return <RowSkeleton columns={communicationsSnapshotColumns} rows={[1]} />
  }
  return (
    <TableRow
      className={classNames({
        [styles.tableRow]: !message.isSystemMessage,
        [styles.unreadTableRow]: isUnread,
        [styles.systemMessageRow]: message.isSystemMessage,
      })}
      key={message.id}
    >
      <TableCell className={classNames(styles.expandCell, styles.noBorder)} />
      <MessageDateCell noBottomBorder lastMessageDate={message.timestamp} />
      <SubjectCell
        noBottomBorder
        conversationId={conversation.id}
        isDraft={message.status === MessageStatus.DRAFT}
        isPlainText={isPlainText}
        isUnread={isUnread}
        message={message.isSystemMessage ? '' : message.body}
        messageId={message.id}
        subject={SubjectLine}
      />
      <TableCell className={styles.noBorder} style={{ width: 0, padding: 0 }} />
    </TableRow>
  )
}

export default MessageRow
